import { useState } from "react";
import { C, S, BASE } from "../constants";
import TaskModal, { MemberAvatar } from "../components/TaskModal";
import Img from "../components/Img";
import ProdLink from "../components/ProdLink";

const COLUMNS = [
  { v: "todo",        l: "Нові",          color: C.text3 },
  { v: "in_progress", l: "В роботі",      color: C.blue },
  { v: "review",      l: "На перевірці",  color: C.yellow },
  { v: "done",        l: "Виконано",      color: C.green },
];

const PRIORITY_COLOR = {
  low:      C.text3,
  medium:   C.blue,
  high:     C.yellow,
  critical: C.pink,
};

const PRIORITY_LABEL = {
  low:      "Низький",
  medium:   "Середній",
  high:     "Високий",
  critical: "Критичний",
};

function formatDate(iso) {
  if (!iso) return null;
  const d = new Date(iso + "T00:00:00");
  const today = new Date();
  today.setHours(0,0,0,0);
  const diff = Math.round((d - today) / 86400000);
  if (diff < 0) return { label: `${Math.abs(diff)}д тому`, overdue: true };
  if (diff === 0) return { label: "Сьогодні", overdue: false };
  if (diff === 1) return { label: "Завтра", overdue: false };
  return { label: d.toLocaleDateString("uk-UA", { day: "numeric", month: "short" }), overdue: false };
}

function TaskCard({ task, members, onOpen, onMoveLeft, onMoveRight, colIndex }) {
  const assignee = members.find(m => m.id === task.assignee_id);
  const pColor = PRIORITY_COLOR[task.priority] || C.blue;
  const dateInfo = formatDate(task.due_date);

  return (
    <div
      onClick={() => onOpen(task)}
      style={{
        background: C.bg3, border: `1px solid ${C.border}`,
        borderRadius: 10, padding: "12px 12px 10px",
        marginBottom: 8, cursor: "pointer",
        borderLeft: `3px solid ${pColor}`,
        transition: "border-color .15s, box-shadow .15s",
      }}
      onMouseEnter={e => e.currentTarget.style.boxShadow = `0 4px 16px rgba(0,0,0,.4)`}
      onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}
    >
      {/* Product */}
      {task.product_rozetka_id && (
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
          {task.product_image && <Img src={task.product_image} size={20} />}
          <div style={{ flex: 1, minWidth: 0 }}>
            <ProdLink name={task.product_name} productId={task.product_rozetka_id} style={{ fontSize: 10, color: C.blue, fontWeight: 400 }} />
          </div>
        </div>
      )}

      {/* Title */}
      <div style={{ fontSize: 13, fontWeight: 600, color: C.text1, marginBottom: 8, lineHeight: 1.35 }}>
        {task.title}
      </div>

      {/* Footer */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {assignee && <MemberAvatar member={assignee} size={20} />}
          {dateInfo && (
            <span style={{ fontSize: 10, color: dateInfo.overdue ? C.pink : C.text3, whiteSpace: "nowrap" }}>
              {dateInfo.overdue ? "⚠ " : ""}{dateInfo.label}
            </span>
          )}
        </div>
        {/* Move buttons */}
        <div style={{ display: "flex", gap: 2 }} onClick={e => e.stopPropagation()}>
          {colIndex > 0 && (
            <button onClick={() => onMoveLeft(task)} style={{
              background: "none", border: `1px solid ${C.border}`, borderRadius: 4,
              color: C.text3, fontSize: 10, cursor: "pointer", padding: "2px 5px", lineHeight: 1,
            }}>←</button>
          )}
          {colIndex < COLUMNS.length - 1 && (
            <button onClick={() => onMoveRight(task)} style={{
              background: "none", border: `1px solid ${C.border}`, borderRadius: 4,
              color: C.text3, fontSize: 10, cursor: "pointer", padding: "2px 5px", lineHeight: 1,
            }}>→</button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function TasksPage({ tasks, members, tasksLoading, token, reload }) {
  const [modalTask, setModalTask] = useState(null);   // null=closed, {}=new, task=edit
  const [filterAssignee, setFilterAssignee] = useState(null);
  const [filterPriority, setFilterPriority] = useState(null);
  const [teamTab, setTeamTab] = useState(false);
  const [newMemberName, setNewMemberName] = useState("");
  const [newMemberColor, setNewMemberColor] = useState("#6366f1");
  const [memberSaving, setMemberSaving] = useState(false);

  const filtered = (tasks || []).filter(t => {
    if (filterAssignee && t.assignee_id !== filterAssignee) return false;
    if (filterPriority && t.priority !== filterPriority) return false;
    return true;
  });

  async function handleSaveTask(data) {
    const isEdit = !!modalTask?.id;
    const url = isEdit ? `${BASE}/api/tasks/${modalTask.id}` : `${BASE}/api/tasks`;
    const method = isEdit ? "PATCH" : "POST";
    await fetch(url, {
      method,
      headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
      body: JSON.stringify(data),
    });
    setModalTask(null);
    reload();
  }

  async function handleDeleteTask(id) {
    await fetch(`${BASE}/api/tasks/${id}`, {
      method: "DELETE",
      headers: { Authorization: "Bearer " + token },
    });
    setModalTask(null);
    reload();
  }

  async function handleMove(task, newStatus) {
    await fetch(`${BASE}/api/tasks/${task.id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
      body: JSON.stringify({ status: newStatus }),
    });
    reload();
  }

  async function handleAddMember() {
    if (!newMemberName.trim()) return;
    setMemberSaving(true);
    await fetch(`${BASE}/api/members`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
      body: JSON.stringify({ name: newMemberName.trim(), color: newMemberColor }),
    });
    setMemberSaving(false);
    setNewMemberName("");
    reload();
  }

  async function handleDeleteMember(id) {
    await fetch(`${BASE}/api/members/${id}`, {
      method: "DELETE",
      headers: { Authorization: "Bearer " + token },
    });
    reload();
  }

  const MEMBER_COLORS = ["#6366f1","#34d399","#f472b6","#fbbf24","#60a5fa","#a78bfa","#ef4444","#fb923c"];

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        <span style={{ fontSize: 20, fontWeight: 800 }}>Задачі</span>

        <div style={{ display: "flex", gap: 4, marginLeft: 8 }}>
          <button onClick={() => setTeamTab(false)} style={{
            ...S.btn(!teamTab), padding: "5px 12px",
          }}>Дошка</button>
          <button onClick={() => setTeamTab(true)} style={{
            ...S.btn(teamTab), padding: "5px 12px",
          }}>Команда</button>
        </div>

        {!teamTab && (
          <>
            {/* Assignee filter */}
            <select
              value={filterAssignee || ""}
              onChange={e => setFilterAssignee(e.target.value ? Number(e.target.value) : null)}
              style={{ ...S.input, fontSize: 12, padding: "5px 10px" }}
            >
              <option value="">Всі виконавці</option>
              {(members || []).map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>

            {/* Priority filter */}
            <select
              value={filterPriority || ""}
              onChange={e => setFilterPriority(e.target.value || null)}
              style={{ ...S.input, fontSize: 12, padding: "5px 10px" }}
            >
              <option value="">Всі пріоритети</option>
              {Object.entries(PRIORITY_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </>
        )}

        <button
          onClick={() => setModalTask({})}
          style={{
            marginLeft: "auto", padding: "7px 16px", borderRadius: 9,
            background: C.blue, color: "#fff", border: "none",
            fontSize: 13, fontWeight: 700, cursor: "pointer",
          }}
        >+ Нова задача</button>
      </div>

      {/* Team management tab */}
      {teamTab ? (
        <div style={{ ...S.card, maxWidth: 480 }}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 16 }}>Члени команди</div>

          {/* Existing members */}
          {(members || []).map(m => (
            <div key={m.id} style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: "8px 0", borderBottom: `1px solid ${C.border}`,
            }}>
              <MemberAvatar member={m} size={32} />
              <span style={{ flex: 1, fontSize: 13, color: C.text1 }}>{m.name}</span>
              <button onClick={() => handleDeleteMember(m.id)} style={{
                background: "none", border: "none", color: C.text3,
                fontSize: 12, cursor: "pointer",
              }}>Видалити</button>
            </div>
          ))}

          {/* Add new member */}
          <div style={{ marginTop: 20 }}>
            <div style={{ fontSize: 11, color: C.text3, marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.5px" }}>Додати члена</div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <input
                value={newMemberName}
                onChange={e => setNewMemberName(e.target.value)}
                onKeyDown={e => e.key === "Enter" && handleAddMember()}
                placeholder="Ім'я члена команди"
                style={{ ...S.input, flex: 1, minWidth: 180 }}
              />
              {/* Color picker */}
              <div style={{ display: "flex", gap: 4 }}>
                {MEMBER_COLORS.map(c => (
                  <div key={c} onClick={() => setNewMemberColor(c)} style={{
                    width: 20, height: 20, borderRadius: "50%", background: c, cursor: "pointer",
                    border: `2px solid ${newMemberColor === c ? "#fff" : "transparent"}`,
                    boxSizing: "border-box",
                  }} />
                ))}
              </div>
              <button
                onClick={handleAddMember}
                disabled={!newMemberName.trim() || memberSaving}
                style={{
                  padding: "8px 16px", borderRadius: 9, background: C.blue,
                  color: "#fff", border: "none", fontSize: 13, fontWeight: 700,
                  cursor: newMemberName.trim() ? "pointer" : "default",
                }}
              >{memberSaving ? "..." : "Додати"}</button>
            </div>
          </div>
        </div>
      ) : (
        /* Kanban board */
        tasksLoading ? (
          <div style={{ textAlign: "center", color: C.text3, padding: 60 }}>Завантаження...</div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, alignItems: "start" }}>
            {COLUMNS.map((col, colIndex) => {
              const colTasks = filtered.filter(t => t.status === col.v);
              return (
                <div key={col.v} style={{
                  background: C.bg1, borderRadius: 12, padding: "12px 10px",
                  border: `1px solid ${C.border}`, minHeight: 200,
                }}>
                  {/* Column header */}
                  <div style={{
                    display: "flex", alignItems: "center", gap: 8, marginBottom: 12,
                    paddingBottom: 10, borderBottom: `1px solid ${C.border}`,
                  }}>
                    <div style={{ width: 8, height: 8, borderRadius: "50%", background: col.color, flexShrink: 0 }} />
                    <span style={{ fontWeight: 700, fontSize: 12, color: col.color, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                      {col.l}
                    </span>
                    <span style={{
                      marginLeft: "auto", background: col.color + "20", color: col.color,
                      borderRadius: 10, padding: "1px 7px", fontSize: 11, fontWeight: 700,
                    }}>{colTasks.length}</span>
                  </div>

                  {/* Cards */}
                  {colTasks.map(task => (
                    <TaskCard
                      key={task.id}
                      task={task}
                      members={members || []}
                      colIndex={colIndex}
                      onOpen={setModalTask}
                      onMoveLeft={t => handleMove(t, COLUMNS[colIndex - 1].v)}
                      onMoveRight={t => handleMove(t, COLUMNS[colIndex + 1].v)}
                    />
                  ))}

                  {/* Add in column */}
                  <button
                    onClick={() => setModalTask({ status: col.v })}
                    style={{
                      width: "100%", padding: "6px 0", borderRadius: 7,
                      background: "transparent", border: `1px dashed ${C.border}`,
                      color: C.text3, fontSize: 12, cursor: "pointer", marginTop: 4,
                    }}
                  >+ Додати</button>
                </div>
              );
            })}
          </div>
        )
      )}

      {/* Modal */}
      {modalTask !== null && (
        <TaskModal
          task={modalTask?.id ? modalTask : null}
          members={members || []}
          token={token}
          onSave={handleSaveTask}
          onDelete={handleDeleteTask}
          onClose={() => setModalTask(null)}
        />
      )}
    </div>
  );
}
